/* EditorTabRenderer.js */

"use strict";

class EditorTabRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("editor-tab-gl");
        this._rootElem.component = this._model;
        this._titleElem = document.createElement("span");
        this._titleDirtyElem = document.createElement("span");
        this._titleDirtyElem.setAttribute('class', 'dirty lm_title editor-title');
        this._onEditorActionIconClicked = this._onEditorActionIconClicked.bind(this);
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */

    /********************************************/
    _registerListeners() {
        this._model.on("EditorActionIconTypeChanged", this._onEditorActionIconTypeChanged = this._onEditorActionIconTypeChanged.bind(this));
        this._model.on("EditorActionIconNameChanged", this._onEditorActionIconNameChanged = this._onEditorActionIconNameChanged.bind(this));
        this._model.on("EditorActionIconTitleChanged", this._onEditorActionIconTitleChanged = this._onEditorActionIconTitleChanged.bind(this));
        this._model.on("EditorIconTypeChanged", this._onEditorIconTypeChanged = this._onEditorIconTypeChanged.bind(this));
        this._model.on("EditorIconNameChanged", this._onEditorIconNameChanged = this._onEditorIconNameChanged.bind(this));
        this._model.on("TitleChanged", this._onTitleChanged = this._onTitleChanged.bind(this));
        this._model.on("DirtyChanged", this._onDirtyChanged = this._onDirtyChanged.bind(this));
    }

    _unregisterListeners() {
        this._model.removeAllListeners("EditorIconTypeChanged", this._onEditorIconTypeChanged);
        this._model.removeAllListeners("EditorIconNameChanged", this._onEditorIconNameChanged);
        this._model.removeAllListeners("TitleChanged", this._onTitleChanged);
        this._model.removeAllListeners("DirtyChanged", this._onDirtyChanged);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */

    /*******************************************/
    shutdown() {
        this._unregisterListeners();
        let promArr = [Promise.resolve()];

        return Promise.all(promArr)
            .then(() => {
                this._model = undefined;
                this._rootElem.remove();
                this._titleDirtyElem.remove();
                this._rootElem = undefined;
                this._titleDirtyElem = undefined;
                this._titleElem = undefined;
            });
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve/*,reject*/) => {
            if (!this._rootElem.hasChildNodes()) {
                this._titleElem.classList.add("lm_title", "editor-title");
                this._rootElem.appendChild(this._titleElem);
                this._rootElem.appendChild(this._titleDirtyElem);
            }
            this._renderTitle();
            this._renderIcon();
            this._renderActionIcon();
            resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */

    /*******************************************/
    _renderTitle() {
        let titleText = this._model.getTitle();
        this.rootElement.setAttribute("title", titleText);
        this._titleElem.innerText = titleText;

        if (this._model.isDirty()) {
            this._titleDirtyElem.innerText = '*';
        } else {
            this._titleDirtyElem.innerText = '';
        }
    }

    _renderIcon() {
        let iconName = this._model.getEditorIconName();
        let iconType = this._model.getEditorIconType();
        let icon = document.createElement(iconType);
        icon.dataset.icon = iconName;
        icon.setAttribute('data-dark-theme', 'true');
        icon.classList.add("icon", "editor-icon");
        let prevIcon = this._rootElem.querySelector(".editor-icon");
        if (prevIcon) {
            prevIcon.remove();
        }
        this._rootElem.insertBefore(icon, this._rootElem.firstChild);
    }

    _renderActionIcon() {
        let prevIcon = this._rootElem.querySelector(".editor-action-icon");
        if (prevIcon) {
            prevIcon.removeEventListener('click', this._onEditorActionIconClicked);
            prevIcon.remove();
        }
        let iconName = this._model.getEditorActionIconName();
        if (iconName) {
            let iconType = this._model.getEditorActionIconType();
            let icon = document.createElement(iconType);
            icon.dataset.icon = iconName;
            icon.setAttribute('data-dark-theme', 'true');
            icon.setAttribute('title', this.model.editorActionIconTitle);
            icon.classList.add("icon", "editor-action-icon");
            icon.addEventListener('click', this._onEditorActionIconClicked);
            this._rootElem.appendChild(icon, this._rootElem);
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */

    /*******************************************/
    _onEditorActionIconTypeChanged() {
        this._renderActionIcon();
    }

    _onEditorActionIconNameChanged() {
        this._renderActionIcon();
    }

    _onEditorActionIconTitleChanged() {
        this._renderActionIcon();
    }

    _onEditorActionIconClicked() {
        this.model._triggerEditorActionIconClicked();
    }

    _onEditorIconTypeChanged() {
        this._renderIcon();
    }

    _onEditorIconNameChanged() {
        this._renderIcon();
    }

    _onTitleChanged() {
        this._renderTitle();
    }

    _onDirtyChanged() {
        this._renderTitle();
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = EditorTabRenderer;
